import { ContactType, InteractionContactInput } from '@api/generated/graphql';
import { getOutboundCallerId } from '@api/queries/getOutboundCallerId';
import { DYNAMIC_CALLER_ID_NUMBER } from '@components/OutboundCallerIdSelection/constants';
import { shouldUseSoftPhone } from '@lib/utils/shouldUseSoftPhone';
import DiagnosticsModule, {
  MetricNames,
  MetricTags,
  OperationNames,
  CallDirection,
  CallConnectionErrorTypes,
} from '@modules/DiagnosticsModule';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import { OutboundCallerIdInput } from '@modules/SoftPhoneModule/types';
import ToastModule from '@modules/ToastModule';
import { useCallback, useState } from 'react';
import { USE_DIRECT_LINE } from '@constants/tenantSelection';
import { authState } from '@modules/AuthModule/state';
import { resolveOrgNodeId } from '@modules/AuthModule/lib/resolveOrgNodeId';

export interface OutboundCallProps {
  callerNumber?: string | null;
  phoneNumber: string;
  customerName?: string;
  customerId?: string;
  tenantId?: string | null;
  type?: ContactType;
  customTaskAttributes?: Record<string, unknown>;
  contacts?: InteractionContactInput[];
}

/**
 * Custom hook to handle outbound calls.
 * It provides a function to make outbound calls using the soft phone method.
 * It also handles errors and displays appropriate messages to the user.
 *
 * @returns { outboundCall: Function, isProcessingCall: boolean }
 * @throws {Error} If the call type is invalid or if the phone number is invalid.
 * @throws {Error} If the phone number is invalid when making a soft phone call.
 *
 */
export const useOutboundCall = () => {
  const [mfeCallLoading, setMfeCallLoading] = useState(false);

  const getOutboundCallId = useCallback(
    async (
      phoneNumber: string,
      tenantId?: string | null,
      callerNumber?: string | null,
    ) => {
      const isDynamicCallId =
        callerNumber === DYNAMIC_CALLER_ID_NUMBER || callerNumber === null;
      let outboundCallerId: OutboundCallerIdInput | undefined;

      // Emergency/voicemail numbers bypass outbound caller ID lookup
      if (shouldUseSoftPhone(phoneNumber)) {
        return undefined;
      } else if (!tenantId) {
        // DID/agent personal line is used when tenantId is null
        return {
          number: callerNumber ?? '',
          tenantId: null,
          name: '',
        };
      } else if (!isDynamicCallId && callerNumber) {
        // we have all the information we need
        outboundCallerId = {
          number: callerNumber,
          tenantId: tenantId,
          name: '',
        };
      } else if (isDynamicCallId) {
        // we need to fetch the outbound caller id from the server
        const outboundCallId = await getOutboundCallerId(tenantId, phoneNumber);
        outboundCallerId = outboundCallId
          ? {
              number: outboundCallId,
              tenantId: tenantId,
              name: '',
            }
          : undefined;
      }
      return outboundCallerId;
    },
    [],
  );

  const outboundCall = useCallback(
    async ({
      phoneNumber,
      customerName,
      customerId,
      tenantId,
      type = ContactType.Phone,
      customTaskAttributes = {},
      callerNumber,
      contacts,
    }: OutboundCallProps) => {
      if (type !== ContactType.MobilePhone && type !== ContactType.Phone) {
        console.error('Invalid call type', type);
        throw new Error('Invalid call type');
      }

      if (!phoneNumber) {
        console.error('Phone number is invalid');
        throw new Error('Phone number is invalid');
      }
      try {
        const sanitizedTenantId =
          tenantId === USE_DIRECT_LINE || !tenantId ? undefined : tenantId;
        // Record the dialed customer's tenant org node, as a pick does, so the
        // booking panel opens on this customer and never on a phone search that
        // could swap them. Only with a real tenant: a direct-line call drops the
        // tenant, so the customer's tenant is unknown and no org node is stored
        // (a late call tenant must never be paired with this customer).
        // `customerTenantId` is deliberately not set: it marks a cross-tenant
        // pick and keeps the legacy tenant-less view mounted.
        // assumes one tenant per orgNodeId (the inverse of `resolveTenantId`,
        // which `useInitiateCall` uses to get this tenant from the MFE payload)
        const customerOrgNodeId =
          customerId && sanitizedTenantId
            ? resolveOrgNodeId(
                authState.user?.org.tenants ?? [],
                sanitizedTenantId,
              )
            : undefined;
        const taskAttributes = {
          customerId,
          customerName,
          ...(customerOrgNodeId ? { customerOrgNodeId } : {}),
          contacts,
          ...customTaskAttributes,
        };

        setMfeCallLoading(true);

        const outboundCallerId = await getOutboundCallId(
          phoneNumber,
          tenantId,
          callerNumber,
        );

        if (outboundCallerId) {
          // if the outbound call fails, it will throw an error that will be caught by the catch block.
          await SoftPhoneModule.callNumberWithCallId(
            phoneNumber,
            outboundCallerId,
            customerName,
            sanitizedTenantId,
            taskAttributes,
          );
        } else {
          SoftPhoneModule.callNumber(
            phoneNumber,
            customerName,
            sanitizedTenantId,
            taskAttributes,
          );
        }
        setMfeCallLoading(false);
      } catch (error) {
        setMfeCallLoading(false);
        DiagnosticsModule.incrementCounter(MetricNames.ERROR_TOTAL, {
          [MetricTags.OPERATION_NAME]: OperationNames.CALL_CONNECTION,
          [MetricTags.DIRECTION]: CallDirection.Outbound.toLowerCase(),
          [MetricTags.ERROR_TYPE]: CallConnectionErrorTypes.GENERAL,
        });
        const toastTitle =
          'We encountered an issue while making an outbound call. Please try again.';
        ToastModule.showToast({
          title: toastTitle,
          status: 'critical',
        });
        DiagnosticsModule.reportBackgroundError(
          error,
          'Failed to make an outbound call.',
          {
            extra: {
              outboundCallerId: callerNumber,
              tenantId,
              customerId,
            },
          },
        );
      }
    },
    [getOutboundCallId],
  );

  return {
    outboundCall,
    isProcessingCall: mfeCallLoading,
  };
};
